import * as v from "valibot";
import currency from "currency.js";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { AcquiredDateChoices, ArtworkMedium } from "@generated/graphql";

import { YesNo } from "./csvUtils/enums";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const csvString = v.union([
  v.string(),
  v.pipe(
    v.number(),
    v.transform((v) => v.toString()),
  ),
]);

export const trimmedString = v.pipe(csvString, v.trim());

const currencyConvertor = (inputCurrency: string, isOptional: boolean = false) => {
  const currencyValue = currency(inputCurrency).value;
  if (currencyValue === 0 && !isOptional) {
    return inputCurrency;
  } else {
    return currencyValue;
  }
};

export const MandatoryCurrencyToNumber = v.pipe(
  v.string("Example format of currency: $12345, greater than 0"),
  v.transform((input) => currencyConvertor(input)),
  v.number("Example format of currency: $12345, greater than 0"),
  v.minValue(0.01, "Currency amount must be greater than 0"), // 0 will be used for optional sale price, eg: CMO
);

export const OptionalCurrencyToNumber = v.pipe(
  v.optional(v.string("Example format of currency: $12345")),
  v.transform((input) => {
    if (input === undefined || input === null) {
      return 0;
    }
    return currencyConvertor(input, true);
  }),
  v.number("Example format of currency: $12345"),
);

const replaceSpaces = (s: string): string => s.replaceAll(" ", "_");

export const AcquiredDateChoiceEnum = v.pipe(
  v.string(),
  v.trim(),
  v.transform(replaceSpaces),
  v.toUpperCase(),
  v.enum(AcquiredDateChoices),
);

export const YesNoUnknownEnum = v.pipe(v.string(), v.trim(), v.enum(YesNo));

export const MediumEnum = v.pipe(
  v.string(),
  v.trim(),
  v.transform((input) => input.replace(/ /g, "_").replace(/\W/g, "")),
  v.toUpperCase(),
  v.enum(ArtworkMedium),
);

export const yearIntToDate = v.pipe(
  v.number(),
  v.transform((input) => new Date(input).toISOString()),
  v.string(),
);

export const dateISOString = v.pipe(
  v.string(),
  v.transform((input) => {
    // The business wants to use 'DD/MM/YYYY' format in csv uploads. However Javascript expects date strings passed to Date constructors to be in MM/DD/YYYY format else it returns null.
    // Therefore we need to manually convert the date string from DD/MM/YYYY format to MM/DD/YYYY format before passing to Date constructor.
    const date = dayjs.utc(input.trim(), "DD/MM/YYYY");

    // Check if the date is valid
    if (!date.isValid()) {
      return date.toDate();
    }

    return date.toISOString();
  }),
  v.string("Date needs to be DD/MM/YYYY format"),
);
